import * as z from "zod";
import { ProofRunSnapshot } from "./proofRun";

const ProofWake = z.object({
  skipped_reason: z.literal("round_cap"),
  managed_pr_id: z.string().nullable(),
  max_rounds: z.number().int().positive().nullable(),
});

/** Stamped by the backend's ProofCompletionMirror when a Proof run settles. */
const ProofReportMetadata = z.intersection(
  ProofRunSnapshot,
  z.object({
    source_message_id: z.string().nullable(),
    /** The child's own words for the reviewer; null when its turn failed. */
    reply: z.string().nullable(),
    /** Present while the run's parent wake is skipped; a malformed block hides the strip, not the report. */
    wake: ProofWake.optional().catch(undefined),
  }),
);
export type ProofReportMetadata = z.infer<typeof ProofReportMetadata>;

export function isProofReportMetadata(
  run: ProofRunSnapshot | ProofReportMetadata,
): run is ProofReportMetadata {
  return "source_message_id" in run;
}

// One parse per raw object, so a report keeps its identity across renders
// and index rebuilds can tell "unchanged" from "changed" by reference.
const parsedByRaw = new WeakMap<object, ProofReportMetadata | null>();

/** Anything shaped like the mirrored `proof_report` block; null when malformed. */
export function parseProofReportMetadata(
  raw: object,
): ProofReportMetadata | null {
  let parsed = parsedByRaw.get(raw);
  if (parsed === undefined) {
    const result = ProofReportMetadata.safeParse(raw);
    parsed = result.success ? result.data : null;
    parsedByRaw.set(raw, parsed);
  }
  return parsed;
}

export function proofReportMetadata(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): ProofReportMetadata | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.proof_report;
  if (typeof raw !== "object" || raw === null) return null;
  return parseProofReportMetadata(raw);
}

/** Display-only row, not a turn of this session: working-state and turn-anchoring scans skip it. */
export function isProofReportMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return proofReportMetadata(message) !== null;
}
